import api from './api';

const managementRequest = (config = {}) => ({
  _authContext: 'management',
  ...config,
});

const normalizePaginated = (response) => ({
  data: response.data?.data || [],
  pageIndex: response.data?.pageIndex ?? 0,
  itemsPerPage: response.data?.itemsPerPage ?? 0,
  totalItems: response.data?.totalItems ?? 0,
});

export const getPaymentOperationsSummary = async () => {
  const response = await api.get('/payment-operations/summary', managementRequest());
  return response.data;
};

export const searchPaymentWebhookEvents = async ({
  status,
  eventType,
  connectedAccountId,
  staleProcessingOnly = false,
  pageIndex = 0,
  itemsPerPage = 25,
} = {}) => {
  const response = await api.get('/payment-operations/webhook-events', managementRequest({
    params: {
      status,
      eventType,
      connectedAccountId,
      staleProcessingOnly,
      pageIndex,
      itemsPerPage,
    },
  }));

  return normalizePaginated(response);
};

export const getPaymentWebhookEventDetail = async (id) => {
  const response = await api.get(`/payment-operations/webhook-events/${id}`, managementRequest());
  return response.data;
};

export const retryPaymentWebhookEvent = async (id) => {
  const response = await api.post(`/payment-operations/webhook-events/${id}/retry`, null, managementRequest());
  return response.data;
};

export const getAppointmentPaymentExceptions = async ({ pageIndex = 0, itemsPerPage = 25 } = {}) => {
  const response = await api.get('/payment-operations/appointment-exceptions', managementRequest({
    params: { pageIndex, itemsPerPage },
  }));

  return normalizePaginated(response);
};

export const getStoreSubscriptionExceptions = async ({ pageIndex = 0, itemsPerPage = 25 } = {}) => {
  const response = await api.get('/payment-operations/store-subscription-exceptions', managementRequest({
    params: { pageIndex, itemsPerPage },
  }));

  return normalizePaginated(response);
};

export const runStripeReconciliation = async () => {
  const response = await api.post('/payment-operations/reconcile', null, managementRequest());
  return response.data;
};

export const getPaymentMonitoringStatus = async () => {
  const response = await api.get('/payment-operations/monitoring', managementRequest());
  return response.data;
};

export const runPaymentMonitoringEvaluation = async () => {
  const response = await api.post('/payment-operations/monitoring/evaluate', null, managementRequest());
  return response.data;
};

export const searchPaymentDisputes = async ({
  requiresActionOnly = false,
  status,
  pageIndex = 0,
  itemsPerPage = 25,
} = {}) => {
  const response = await api.get('/payment-operations/disputes', managementRequest({
    params: {
      requiresActionOnly,
      status,
      pageIndex,
      itemsPerPage,
    },
  }));

  return normalizePaginated(response);
};

export const getPaymentDisputeDetail = async (id) => {
  const response = await api.get(`/payment-operations/disputes/${id}`, managementRequest());
  return response.data;
};

export const savePaymentDisputeEvidence = async (id, payload) => {
  const response = await api.put(`/payment-operations/disputes/${id}/evidence`, payload, managementRequest());
  return response.data;
};

export const uploadPaymentDisputeEvidenceAttachment = async (id, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `/payment-operations/disputes/${id}/evidence/attachments`,
    formData,
    managementRequest({
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  );

  return response.data;
};

export const submitPaymentDisputeEvidence = async (id) => {
  const response = await api.post(`/payment-operations/disputes/${id}/evidence/submit`, null, managementRequest());
  return response.data;
};

export const syncPaymentDispute = async (id) => {
  const response = await api.post(`/payment-operations/disputes/${id}/sync`, null, managementRequest());
  return response.data;
};

export const searchPaymentRefunds = async ({
  appointmentId,
  status,
  pageIndex = 0,
  itemsPerPage = 25,
} = {}) => {
  const response = await api.get('/payment-operations/refunds', managementRequest({
    params: {
      appointmentId,
      status,
      pageIndex,
      itemsPerPage,
    },
  }));

  return normalizePaginated(response);
};

export const getPaymentRefundDetail = async (id) => {
  const response = await api.get(`/payment-operations/refunds/${id}`, managementRequest());
  return response.data;
};

export const getAppointmentPaymentRefundContext = async (appointmentId) => {
  const response = await api.get(
    `/payment-operations/appointments/${appointmentId}/refund-context`,
    managementRequest(),
  );
  return response.data;
};

export const issueAppointmentPaymentRefund = async (appointmentId, payload) => {
  const response = await api.post(
    `/payment-operations/appointments/${appointmentId}/refunds`,
    payload,
    managementRequest(),
  );
  return response.data;
};

export const syncPaymentRefund = async (id) => {
  const response = await api.post(`/payment-operations/refunds/${id}/sync`, null, managementRequest());
  return response.data;
};

export const getCustomerMembershipExceptions = async ({ pageIndex = 0, itemsPerPage = 25 } = {}) => {
  const response = await api.get('/payment-operations/customer-membership-exceptions', managementRequest({
    params: { pageIndex, itemsPerPage },
  }));

  return normalizePaginated(response);
};
